import { MarkdownContent } from '../../ui/markdown-content';
import { Skeleton } from '../../ui/skeleton';
import { useVfsContent } from '@/hooks/useVfsContent';
import type { AuiRendererProps } from '../registry';
import { cleanDisplayText } from '@/utils/display-text';

// Heading variants carry an intrinsic top margin so a section header always
// separates from the content above it, regardless of the surrounding layout's
// `gap`. `[&:not(:first-child)]` collapses that margin when the heading is the
// first child of its parent (e.g. a card title), so it only adds space when it
// actually follows a sibling. In a flex Column the margin stacks on top of the
// base gap — giving section headers more room above than below, which is the
// asymmetry a uniform gap can't express.
const variantClasses: Record<string, string> = {
  h1: 'text-2xl font-bold text-foreground [&:not(:first-child)]:mt-6',
  h2: 'text-xl font-semibold text-foreground [&:not(:first-child)]:mt-5',
  h3: 'text-lg font-semibold text-foreground [&:not(:first-child)]:mt-4',
  h4: 'text-base font-medium text-foreground [&:not(:first-child)]:mt-3',
  h5: 'text-sm font-medium text-foreground',
  h6: 'text-xs font-medium text-foreground',
  p: 'text-sm text-foreground',
  caption: 'text-xs text-muted-foreground',
  muted: 'text-sm text-muted-foreground',
};

const bodyVariants = new Set(['p', 'muted', 'caption']);

export function AuiText({ node }: AuiRendererProps) {
  const props = node.component.props ?? {};
  const inlineText = (props.text as string) ?? (props.content as string) ?? '';
  const textUrl = props.textUrl as string | undefined;
  const fetched = useVfsContent(textUrl);
  const text = textUrl ? (fetched.data ?? '') : inlineText;

  const variant = (props.variant as string) ?? 'p';
  const className = variantClasses[variant] ?? variantClasses.p;

  if (textUrl && fetched.loading) {
    return <Skeleton className="h-4 w-3/4" data-testid="aui-text" />;
  }

  if (textUrl && fetched.error) {
    return (
      <p className="text-sm text-destructive" data-testid="aui-text">
        Failed to load text: {fetched.error.message}
      </p>
    );
  }

  if (bodyVariants.has(variant)) {
    const size = variant === 'caption' || variant === 'muted' ? 'xs' : 'sm';
    return (
      <div className={className} data-testid="aui-text">
        <MarkdownContent size={size}>{text}</MarkdownContent>
      </div>
    );
  }

  return (
    <p className={className} data-testid="aui-text">
      {cleanDisplayText(text)}
    </p>
  );
}
